import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Tooltip, makeStyles } from 'design-system';
import { InstructionComponentType } from '@scale/shared/defaults';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useProjectInstructionContext } from '../projectInstructionContext';
import SearchBar from '../components/searchBar';
import SideNavigation from '../components/sideNavigation';
import Timer from '../components/timer';
import Details from '../components/details';
import SearchPage from '../components/searchPage';
import Banner from '../components/banner';
import { iconPropsByType } from '../constants';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { SelectedPathType } from '../types';
import ComparisonToggle from './comparisonToggle';
import { StellarTasksModal } from '../../StellarTask/components/StellarTasksModal';

// this Side Panel Instruction component is shared in both small screen view and side panel view
const InstructionSidePanel = ({
  isOpen,
  onClose,
  projectId,
  assignmentId,
}: {
  isOpen?: boolean;
  onClose?: () => void;
  projectId?: string;
  assignmentId?: string;
}) => {
  const {
    title,
    components,
    selectedPath,
    setSelectedPath,
    selectedSection,
    isMobileView,
    enableSidePanel,
    searchResult,
    setSearchResult,
  } = useProjectInstructionContext();
  const [biggerSidePanel, setBiggerSidePanel] = useState(false);

  const classes = useStyles({ isOpen, isMobileView, enableSidePanel, biggerSidePanel });

  const [navState, setNavState] = useState<{
    backward: SelectedPathType[];
    forward: SelectedPathType[];
  }>({
    backward: [],
    forward: [],
  });

  useEffect(() => {
    const expBar = document.querySelector('.bar-container') as HTMLElement;
    if (expBar) {
      if (isOpen) {
        expBar.style.display = 'none';
      } else {
        expBar.style.display = 'flex';
      }
    }
  });

  if (_.isEmpty(components)) {
    return (
      <div style={{ margin: '0 auto', width: 'fit-content', display: isOpen ? 'block' : 'none' }}>
        <br />
        <br />
        <h2>Instructions have not been created for this project yet.</h2>
        <img
          style={{
            position: 'absolute',
            bottom: 0,
            transform: 'translate(70%,-200%)',
            width: '200px',
          }}
          src={`/static/images/unavailable/group.svg`}
          width="600px"
        />
      </div>
    );
  }

  const handleSetSelectedPath = (path: SelectedPathType) => {
    const { backward } = navState;
    backward.push(selectedPath);
    setSelectedPath(path);
    setNavState({ ...navState, backward: [...backward], forward: [] });
  };

  const handleBackward = () => {
    const { backward, forward } = navState;
    if (!backward.length) {
      return;
    }
    let history = backward.pop();
    while (history === backward[backward.length - 1]) {
      history = backward.pop();
    }
    forward.unshift(selectedPath);
    setSelectedPath(history as SelectedPathType);
    setNavState({ backward: [...backward], forward: [...forward] });
  };

  const handleForward = () => {
    const { backward, forward } = navState;
    if (!forward.length) {
      return;
    }
    let history = forward.shift();
    while (history === forward[0]) {
      history = forward.shift();
    }
    backward.push(selectedPath);
    setSelectedPath(history as SelectedPathType);
    setNavState({ backward: [...backward], forward: [...forward] });
  };

  const typesGroup: Array<InstructionComponentType | 'ToolTutorials'> = [];

  components?.forEach(({ nodeType }) => {
    if (!typesGroup.includes(nodeType)) {
      typesGroup.push(nodeType);
    }
  });

  let headerTitle = <div>{title ?? 'Instructions'}</div>;
  let headerType = null;

  if (selectedSection?.title) {
    const isWorkflow = selectedSection?.title?.toLowerCase()?.includes('workflow');
    const { iconName, iconColor } = isWorkflow
      ? iconPropsByType.workflow
      : selectedSection.type
      ? iconPropsByType[selectedSection.type] || { iconName: '' as IconProp, iconColor: '' }
      : { iconName: '' as IconProp, iconColor: '' };
    headerType = (
      <span className={classes.typeIcon} style={{ background: iconColor + '26' }}>
        {/* append 26 to change the opacity to 15% */}
        <FontAwesomeIcon icon={iconName} color={iconColor} />
      </span>
    );

    headerTitle = (
      <div>
        {headerType}
        {_.startCase(selectedSection.title)}
      </div>
    );
  }

  return (
    <div className={classes.sidePanelContainer}>
      <div className={classes.header}>
        <Tooltip arrow title="Table of contents" placement="bottom">
          <button
            className={classes.btn}
            onClick={() => {
              handleSetSelectedPath([]);
              setSearchResult(null);
            }}
          >
            <FontAwesomeIcon icon="home" color="var(--color-RemoGray50)" />
          </button>
        </Tooltip>

        <strong className={classes.title}>
          {headerTitle}
          {!!selectedSection?.waitSeconds && <Timer waitSeconds={selectedSection.waitSeconds} />}
        </strong>

        {enableSidePanel && (
          <div className={classes.rightButtons}>
            <Tooltip arrow title="Expand width" placement="bottom">
              <button
                className={classes.btn}
                style={{ marginRight: '4px' }}
                onClick={() => {
                  setBiggerSidePanel(!biggerSidePanel);
                }}
              >
                {biggerSidePanel ? (
                  <FontAwesomeIcon icon="chevron-double-left" color="var(--color-RemoGray50)" />
                ) : (
                  <FontAwesomeIcon icon="chevron-double-right" color="var(--color-RemoGray50)" />
                )}
              </button>
            </Tooltip>

            <Tooltip arrow title="Open in new tab" placement="bottom">
              <button
                className={classes.btn}
                onClick={() => {
                  window.open(
                    window.location.origin +
                      `/instruction/${projectId}?assignmentId=${assignmentId}`,
                    '_blank',
                  );
                }}
              >
                <FontAwesomeIcon icon="share-square" color="var(--color-RemoGray50)" />
              </button>
            </Tooltip>

            <button className={classes.btn} onClick={onClose}>
              &#10005;
            </button>
          </div>
        )}
      </div>
      {
        typeof isOpen === 'boolean' ? (
          <ComparisonToggle />
        ) : (
          <Banner />
        ) /* don't show banner for side panel view */
      }
      <div className={classes.wrapper}>
        <button
          className={classes.navBtn}
          style={{
            color: navState.backward?.length
              ? 'var(--color-RemoGray50)'
              : 'var(--color-RemoGray20)',
          }}
          onClick={handleBackward}
        >
          <FontAwesomeIcon icon="angle-left" size="2x" />
        </button>
        <button
          className={classes.navBtn}
          style={{
            color: navState.forward?.length ? 'var(--color-RemoGray50)' : 'var(--color-RemoGray20)',
          }}
          onClick={handleForward}
        >
          <FontAwesomeIcon icon="angle-right" size="2x" />
        </button>
        <SearchBar />
      </div>
      {searchResult ? (
        <SearchPage />
      ) : selectedSection ? (
        <Details contentData={selectedSection} />
      ) : (
        <SideNavigation handleSetSelectedPath={handleSetSelectedPath} />
      )}
      {projectId && (
        // Don't render button/modal if project not valid
        <div className={classes.btnwrapper}>
          <StellarTasksModal projectId={projectId} />
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  sidePanelContainer: {
    width: ({
      isOpen,
      isMobileView,
      enableSidePanel,
      biggerSidePanel,
    }: {
      isOpen?: boolean;
      isMobileView?: boolean;
      enableSidePanel?: boolean;
      biggerSidePanel?: boolean;
    }) => {
      return isMobileView && !enableSidePanel ? '100%' : isOpen ? (biggerSidePanel ? 800 : 320) : 0;
    },
    display: ({ isOpen, isMobileView }: { isOpen?: boolean; isMobileView?: boolean }) => {
      return isOpen || isMobileView ? 'inline-block' : 'none';
    },
    borderRight: `1px solid ${theme.colors.gray05}`,
    transition: 'width 0.5s',
    paddingBottom: 190,
    overflow: 'hidden',
    maxHeight: '90vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 7px 10px 10px',
    color: theme.colors.gray75,
    borderBottom: `1px solid ${theme.colors.gray05}`,
    '& > strong': {
      marginRight: 'auto',
    },
  },
  rightButtons: {
    display: 'flex',
    gap: '0.25rem',
  },
  btn: {
    background: 'none',
    color: theme.colors.gray50,
    border: 'none',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer',
    outline: 'inherit',
    frontWeight: 500,
    marginRight: 5,
    '&:hover': {
      color: theme.colors.gray75,
    },
  },

  wrapper: {
    display: 'flex',
  },

  btnwrapper: {
    display: 'flex',
    paddingLeft: 12,
  },

  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '1',

    '& > div:first-child': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },

  typeIcon: {
    width: 15,
    height: 15,
    borderRadius: 3,
    fontSize: 10,
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },

  navBtn: {
    border: 'none',
    background: 'none',
    borderRadius: 3,
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));
export default InstructionSidePanel;
